import Immutable from 'immutable';
import {local} from './local';
import {buildFetch} from './fetch';
import {createError} from './errors';

import config from 'config';

function capitalize(str) {
  if (!str) {
    return '';
  }

  const elab = str.toString();

  return elab.charAt(0).toUpperCase() + (elab.length > 1 ? elab.slice(1) : '');
}

export function createRepo({
  modelName,
  plural,
  idKey = 'id',
  endpoint = plural,
  getURL,
  isGlobal = false,
  getCurrentCompanyId,
  validateNewItemData,
  buildNewItemData,
  processNewItemData = (newId, data) => ({...data, id: newId}),
  preventDefaultSavingError = false,
}) {
  // Actions
  const uppercaseModelName = modelName.toString().toUpperCase();
  const uppercasePlural = plural.toString().toUpperCase();

  const startListLoading = `START_${uppercasePlural}_LOADING`;
  const listLoadingSuccess = `${uppercasePlural}_LOADING_SUCCESS`;
  const listLoadingError = `${uppercasePlural}_LOADING_ERROR`;
  const startItemLoading = `START_${uppercaseModelName}_LOADING`;
  const itemLoadingSuccess = `${uppercaseModelName}_LOADING_SUCCESS`;
  const itemLoadingError = `${uppercaseModelName}_LOADING_ERROR`;
  const selectItem = `SELECT_${uppercaseModelName}`;
  const toggleItems = `TOGGLE_${uppercasePlural}_SELECTION`;
  const startSelectionDeleting = `START_${uppercasePlural}_SELECTION_DELETING`;
  const selectionDeletingSuccess = `${uppercasePlural}_SELECTION_DELETING_SUCCESS`;
  const selectionDeletingError = `${uppercasePlural}_SELECTION_DELETING_ERROR`;
  const startItemDeleting = `START_${uppercaseModelName}_DELETING`;
  const itemDeletingSuccess = `${uppercaseModelName}_DELETING_SUCCESS`;
  const itemDeletingError = `${uppercaseModelName}_DELETING_ERROR`;
  const startItemSaving = `START_${uppercaseModelName}_SAVING`;
  const itemSavingSuccess = `${uppercaseModelName}_SAVING_SUCCESS`;
  const itemSavingError = `${uppercaseModelName}_SAVING_ERROR`;
  const itemValidationError = `${uppercaseModelName}_VALIDATION_ERROR`;

  const actions = {
    [startListLoading]: startListLoading,
    [listLoadingSuccess]: listLoadingSuccess,
    [listLoadingError]: listLoadingError,
    [startItemLoading]: startItemLoading,
    [itemLoadingSuccess]: itemLoadingSuccess,
    [itemLoadingError]: itemLoadingError,
    [selectItem]: selectItem,
    [toggleItems]: toggleItems,
    [startSelectionDeleting]: startSelectionDeleting,
    [selectionDeletingSuccess]: selectionDeletingSuccess,
    [selectionDeletingError]: selectionDeletingError,
    [startItemDeleting]: startItemDeleting,
    [itemDeletingSuccess]: itemDeletingSuccess,
    [itemDeletingError]: itemDeletingError,
    [startItemSaving]: startItemSaving,
    [itemSavingSuccess]: itemSavingSuccess,
    [itemSavingError]: itemSavingError,
    [itemValidationError]: itemValidationError,
  };

  // Action creators
  const capitalizedModelName = capitalize(modelName);
  const capitalizedPlural = capitalize(plural);

  // List handling
  function startListLoadingAC() {
    return {
      type: startListLoading,
    };
  }
  function listLoadingSuccessAC(data, fromRemote = true) {
    return {
      type: listLoadingSuccess,
      data,
      fromRemote,
    };
  }
  const listLoadingErrorAC = createError(listLoadingError);

  function loadListFromApi(dispatch, getState, persist = true) {
    if (plural === 'tests') {
      const emptyArr = Immutable.fromJS([]);
      dispatch(listLoadingSuccessAC(emptyArr));
    }

    const currentCompanyId = parseInt(getCurrentCompanyId(getState()), 10);
    const fetch = buildFetch(dispatch);
    let url = `${config.api}/${endpoint}?companyId=${currentCompanyId}`;
    if (getURL) {
      url = config.api + '/' + getURL(getState);
    }
    return fetch(url)
      .then(response => {
        const list = response.data[plural];
        if (!list || !Array.isArray(list)) {
          throw new Error(`Invalid ${plural} data`);
        }

        return list;
      })
      .then(list => {
        let storedList = getGlobalList(getState());

        // Compute delta
        const unchangedStoredItemIds = [];
        const newItems = [];
        list.forEach(item => {
          const index = storedList.findIndex(storedItem => storedItem.get(idKey) === item[idKey]);
          if (index === -1) {
            newItems.push(item);
            return;
          }

          const savedItem = storedList.get(index);
          if (!Immutable.is(savedItem, Immutable.fromJS(item))) {
            const savedItemJS = savedItem.toJS();
            newItems.push({...savedItemJS, ...item}); // Merge with previous data
          } else {
            unchangedStoredItemIds.push(item[idKey]);
          }
        });

        // Filter out unchanged data and add new or updated items
        storedList = storedList
          .filter(storedItem => {
            let id = parseInt(storedItem.get(idKey), 10);
            if (Number.isNaN(id)) {
              id = storedItem.get(idKey);
            }
            const companyId = parseInt(storedItem.get('companyId') || -1, 10);

            if (!isGlobal && companyId !== -1 && companyId !== currentCompanyId) {
              return true;
            }

            return unchangedStoredItemIds.indexOf(id) !== -1;
          })
          .concat(Immutable.fromJS(newItems)); // Convert newItems at the end to improve performance

        dispatch(listLoadingSuccessAC(storedList));
        if (persist) {
          local.set(plural, storedList.toJS());
        }
      })
      .catch(error => {
        if (error) {
          dispatch(listLoadingErrorAC(error.message));
        }
      });
  }

  function loadListFromCache(dispatch) {
    const cached = local.get(plural);
    if (cached && Array.isArray(cached) && cached.length > 0) {
      dispatch(listLoadingSuccessAC(Immutable.fromJS(cached), false));

      return true;
    }

    return false;
  }

  function loadListFromStore(dispatch, getState) {
    const list = getList(getState());
    if (list.size > 0) {
      dispatch(listLoadingSuccessAC(list), false);

      return true;
    }

    return false;
  }

  function loadListFromRepo(dispatch, getState, persist = false) {
    dispatch(startListLoadingAC());
    if (!loadListFromStore(dispatch, getState)) {
      loadListFromCache(dispatch);
    }
    return loadListFromApi(dispatch, getState, persist);
  }

  // Item handling
  function startItemLoadingAC() {
    return {
      type: startItemLoading,
    };
  }
  function itemLoadingSuccessAC(data, fromRemote = true) {
    return {
      type: itemLoadingSuccess,
      data,
      fromRemote,
    };
  }
  const itemLoadingErrorAC = createError(itemLoadingError);

  function loadItemFromApi(id, dispatch, getState, persist = false) {
    const fetch = buildFetch(dispatch);
    return fetch(`${config.api}/${endpoint}/${id}`)
      .then(response => {
        const data = Immutable.fromJS(response.data[modelName]);

        dispatch(itemLoadingSuccessAC(data));

        if (persist) {
          const list = getList(getState());
          local.set(plural, list.toJS());
        }

        return data;
      })
      .catch(error => {
        if (error) {
          dispatch(itemLoadingErrorAC(error.message));

          throw error;
        }
      });
  }

  function loadItemFromCache(id, dispatch, getState) {
    const item = getItemById(getState(), id);
    if (item.size > 0) {
      dispatch(itemLoadingSuccessAC(item), false);
      return;
    }

    if (loadListFromCache(dispatch)) {
      loadItemFromCache(id, dispatch, getState);
    }
  }

  function loadItemFromRepo(id, dispatch, getState) {
    dispatch(startItemLoadingAC());
    loadItemFromCache(id, dispatch, getState);

    return loadItemFromApi(id, dispatch, getState);
  }

  // Item selection
  function selectItemAC(id) {
    return {
      type: selectItem,
      id,
    };
  }

  // All item selection toggling
  function toggleItemsAC() {
    return {
      type: toggleItems,
    };
  }

  // Selection deleting
  function startSelectionDeletingAC() {
    return {
      type: startSelectionDeleting,
    };
  }

  function selectionDeletingSuccessAC(selection) {
    return {
      type: selectionDeletingSuccess,
      selection,
    };
  }

  const selectionDeletingErrorAC = createError(selectionDeletingError);

  function deleteSelection(dispatch, getState) {
    const selection = getSelectedItems(getState());
    if (!selection || selection.size === 0) {
      return;
    }

    dispatch(startSelectionDeletingAC());

    const fetch = buildFetch(dispatch);
    return fetch(`${config.api}/${endpoint}/selection`, {
      method: 'DELETE',
      data: {
        selection: selection.toJS(),
      },
    })
      .then(response => {
        const data = response.data;
        if (!data || !data.status) {
          throw new Error(`Could not delete ${plural}: ${JSON.stringify(response)}`);
        }

        dispatch(selectionDeletingSuccessAC(selection));

        return selection;
      })
      .catch(error => {
        if (error) {
          dispatch(selectionDeletingErrorAC(error.message));
        }
      });
  }

  // Item deleting
  function startItemDeletingAC(id) {
    return {
      type: startItemDeleting,
      id,
    };
  }

  function itemDeletingSuccessAC(id) {
    return {
      type: itemDeletingSuccess,
      id,
    };
  }

  const itemDeletingErrorAC = createError(itemDeletingError);

  function deleteItem(id, dispatch) {
    dispatch(startItemDeletingAC(id));

    const fetch = buildFetch(dispatch);

    return fetch(`${config.api}/${endpoint}/${id}`, {
      method: 'DELETE',
    })
      .then(response => {
        const data = response.data;
        if (!data || !data.status) {
          throw new Error(`Could not delete ${modelName} ${id}: ${JSON.stringify(response)}`);
        }

        dispatch(itemDeletingSuccessAC(id));

        return true;
      })
      .catch(error => {
        if (error) {
          const {
            response: {
              data: {code},
            },
          } = error;
          dispatch(itemDeletingErrorAC(error.message, {id, code}));
        }
      });
  }

  // Item saving
  function startItemSavingAC() {
    return {
      type: startItemSaving,
    };
  }

  function itemSavingSuccessAC(data) {
    return {
      type: itemSavingSuccess,
      data,
    };
  }

  const itemSavingErrorAC = createError(itemSavingError);

  function saveItem(data, id, dispatch, getState, updater) {
    dispatch(startItemSavingAC());

    if (validateNewItemData) {
      const error = validateNewItemData(data);
      if (error) {
        dispatch(itemValidationErrorAC(error));
        return;
      }
    }

    const elabData = buildNewItemData({
      ...data,
      companyId: getCurrentCompanyId(getState()),
    });

    const isValidId = Number.isInteger(parseInt(id, 10));
    const fetch = buildFetch(dispatch);

    return fetch(`${config.api}/${endpoint}${isValidId ? '/' + id : ''}`, {
      method: isValidId ? 'PUT' : 'POST',
      data: elabData,
    })
      .then(response => {
        const data = response.data;
        if (!data || !data.status) {
          throw new Error(`Could not create ${modelName}: ${JSON.stringify(response)}`);
        }

        let newId = (data && data[modelName] && data[modelName][idKey]) || id;
        newId = newId && parseInt(newId, 10);
        dispatch(itemSavingSuccessAC(processNewItemData(newId, elabData)));
        if (updater) {
          updater(newId);
        }

        return newId;
      })
      .catch(error => {
        if (error) {
          if (!preventDefaultSavingError) {
            dispatch(itemSavingErrorAC(error.message));
          }

          throw error;
        }
      });
  }

  const itemValidationErrorAC = createError(itemValidationError);

  const actionCreators = {
    [`start${capitalizedPlural}Loading`]: startListLoadingAC,
    [`${plural}LoadingSuccess`]: listLoadingSuccessAC,
    [`${plural}LoadingError`]: listLoadingErrorAC,
    [`load${capitalizedPlural}FromApi`]: loadListFromApi,
    [`load${capitalizedPlural}FromCache`]: loadListFromCache,
    [`load${capitalizedPlural}FromStore`]: loadListFromStore,
    [`load${capitalizedPlural}FromRepo`]: loadListFromRepo,
    [`start${capitalizedModelName}Loading`]: startItemLoadingAC,
    [`${modelName}LoadingSuccess`]: itemLoadingSuccessAC,
    [`${modelName}LoadingError`]: itemLoadingErrorAC,
    [`load${capitalizedModelName}FromApi`]: loadItemFromApi,
    [`load${capitalizedModelName}FromCache`]: loadItemFromCache,
    [`load${capitalizedModelName}FromRepo`]: loadItemFromRepo,
    [`select${capitalizedModelName}`]: selectItemAC,
    [`toggle${capitalizedPlural}`]: toggleItemsAC,
    [`start${capitalizedPlural}SelectionDeleting`]: startSelectionDeletingAC,
    [`${plural}SelectionDeletingSuccess`]: selectionDeletingSuccessAC,
    [`${plural}SelectionDeletingError`]: selectionDeletingErrorAC,
    [`delete${capitalizedPlural}Selection`]: deleteSelection,
    [`start${capitalizedModelName}Deleting`]: startItemDeletingAC,
    [`${modelName}DeletingSuccess`]: itemDeletingSuccessAC,
    [`${modelName}DeletingError`]: itemDeletingErrorAC,
    [`delete${capitalizedModelName}FromRepo`]: deleteItem,
    [`start${capitalizedModelName}Saving`]: startItemSavingAC,
    [`${modelName}SavingSuccess`]: itemSavingSuccessAC,
    [`${modelName}SavingError`]: itemSavingErrorAC,
    [`save${capitalizedModelName}InRepo`]: saveItem,
    [`${modelName}ValidationError`]: itemValidationErrorAC,
  };

  // Reducers
  const reducers = {
    [`handle${capitalizedPlural}Loading`](state, action) {
      return state.set('list', action.data);
    },
    [`handle${capitalizedModelName}Loading`](state, action) {
      if (!action.data) {
        return state;
      }

      const data = Immutable.fromJS(action.data);

      const index = state.get('list').findIndex(item => item.get(idKey) === data.get(idKey));
      if (index >= 0) {
        return state.setIn(['list', index], data);
      }

      return state.set('list', state.get('list').push(data));
    },
    [`handle${capitalizedModelName}Selection`](state, action) {
      if (!action[idKey]) {
        return state;
      }

      const index = state.get('selection').findIndex(itemId => itemId === action[idKey]);
      if (index >= 0) {
        return state.set('selection', state.get('selection').delete(index));
      }

      return state.set('selection', state.get('selection').push(action[idKey]));
    },
    [`handle${capitalizedPlural}SelectionToggle`](state, action) {
      if (state.get('list').size === state.get('selection').size) {
        return state.set('selection', Immutable.List());
      }

      return state.set(
        'selection',
        state.get('list').map(item => item.get(idKey))
      );
    },
    [`handle${capitalizedPlural}SelectionDeletingSuccess`](state, action) {
      if (!action.selection || action.selection.size <= 0) {
        return state;
      }

      return state
        .set(
          'list',
          state
            .get('list')
            .filter(item => action.selection.findIndex(id => item.get(idKey) === id) === -1)
        )
        .set('selection', Immutable.List());
    },
    [`handle${capitalizedModelName}DeletingSuccess`](state, action) {
      if (!action.id && action.id !== 0) {
        return state;
      }

      const index = state.get('list').findIndex(item => item.get(idKey) === action[idKey]);
      if (index < 0) {
        return state;
      }

      return state.deleteIn(['list', index]);
    },
    [`handle${capitalizedModelName}SavingSuccess`](state, action) {
      if (!action.data) {
        return state;
      }

      const data = Immutable.fromJS(action.data);
      const index = state.get('list').findIndex(item => item.get(idKey) === data.get(idKey));
      if (index > -1) {
        return state.setIn(['list', index], data);
      }

      return state.set('list', state.get('list').push(data));
    },
  };

  // Selectors
  function getGlobalList(state) {
    return (state && state.getIn([plural, 'list'])) || Immutable.List();
  }
  function getList(state) {
    const list = getGlobalList(state);
    if (isGlobal) {
      return list;
    }

    const currentCompanyId = parseInt(getCurrentCompanyId(state), 10);
    if (Number.isNaN(currentCompanyId)) {
      return list;
    }

    return list.filter(item => parseInt(item.get('companyId'), 10) === currentCompanyId);
  }
  function getItemById(state, id) {
    return getList(state).find(item => item.get(idKey) === parseInt(id, 10)) || Immutable.Map();
  }
  function getSelectedItems(state) {
    return (state && state.getIn([plural, 'selection'])) || Immutable.List();
  }
  const selectors = {
    [`get${capitalizedPlural}`]: getList,
    [`get${capitalizedModelName}ById`]: getItemById,
    [`get${capitalizedPlural}Selection`]: getSelectedItems,
  };

  function getInitialState(custom = {}) {
    return Immutable.fromJS(
      Object.assign(
        {
          list: [],
          selection: [],
        },
        custom
      )
    );
  }

  return {
    actions,
    actionCreators,
    reducers,
    selectors,
    getInitialState,
  };
}
